import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { Assignment, AutoGraph } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getDevArea as getDevAreaApi,
  getDevAgeBand as getDevAgeBandApi,
  getDevMatter as getDevMatterApi,
  getCheckpoint,
} from "apis";
import { IDevArea, IDevAgeBand, IDevCheckpoint } from "types/grading";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";

export interface IDevMatter {
  id: number;
  code: string;
  areaId: number;
  ageBandId: number;
  sortOrder: number;
  objective: string;
  description: string;
}

const Grading: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [areas, setAreas] = useState<IDevArea[]>([]);
  const [ageBands, setAgeBands] = useState<IDevAgeBand[]>([]);
  const [devMatter, setDevMatter] = useState<IDevMatter[]>([]);
  const [checkpoint, setCheckpoint] = useState<IDevCheckpoint | null>(null);

  const [areaSelected, setAreaSelected] = useState("");
  const [ageBandSelected, setAgeBandSelected] = useState("");

  useEffect(() => {
    Promise.all([getDevAreaApi(), getDevAgeBandApi()]).then(([a, b]) => {
      setAreas(a || []);
      setAgeBands(b || []);
      setAreaSelected((prev) => prev || (a?.length ? String(a[0].id) : ""));
      setAgeBandSelected((prev) => prev || (b?.length ? String(b[0].id) : ""));
    });
  }, [i18n.language]);

  useEffect(() => {
    if (!areaSelected || !ageBandSelected) {
      setDevMatter([]);
      setCheckpoint(null);
      return;
    }
    const areaId = Number(areaSelected);
    const ageBandId = Number(ageBandSelected);
    setLoading(true);
    Promise.all([
      getDevMatterApi({ areaId, ageBandId }),
      getCheckpoint({ areaId, ageBandId }).then((r) => r.data),
    ])
      .then(([matters, cp]) => {
        setDevMatter(matters || []);
        setCheckpoint(cp);
      })
      .finally(() => setLoading(false));
  }, [areaSelected, ageBandSelected, i18n.language]);

  const hasSelection = Boolean(areaSelected && ageBandSelected);

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <AutoGraph sx={{ color: "primary.main", fontSize: 24 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("grading.label")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 2 }}>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle>{t("grading.about_title")}</AlertTitle>
          {t("grading.about_body")}
        </Alert>
        <Card
          variant="outlined"
          sx={{ borderRadius: 3, mb: 2, px: 2.5, py: 2 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontSize: 10,
              mb: 1.5,
              display: "block",
            }}
          >
            {t("grading.development_matters")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("labels.area")}</InputLabel>
                <Select
                  label={t("labels.area")}
                  value={areaSelected}
                  onChange={(e) => setAreaSelected(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {areas.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("labels.age_band")}</InputLabel>
                <Select
                  label={t("labels.age_band")}
                  value={ageBandSelected}
                  onChange={(e) => setAgeBandSelected(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {ageBands.map((b) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {checkpoint && (
            <Box mt={2}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 0.5,
                  display: "block",
                  fontSize: 10,
                }}
              >
                {t("grading.checkpoint")}
              </Typography>
              <Typography variant="body2">{checkpoint.prompt}</Typography>
            </Box>
          )}
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !hasSelection ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={8}
            color="text.secondary"
          >
            <Assignment
              sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
            />
            <Typography variant="h6" gutterBottom>
              {t("grading.select_prompt")}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t("grading.select_hint")}
            </Typography>
          </Box>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2.5}
              py={1.5}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {t("grading.objectives")}
              </Typography>
              <Chip
                label={`${devMatter.length}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }}
              />
            </Box>
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                  >
                    <TableCell sx={{ fontWeight: 700, width: 120 }}>
                      {t("labels.code")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "30%" }}>
                      {t("grading.objective")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("grading.description")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devMatter.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          {t("grading.no_objectives")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    devMatter
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {item.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {item.objective}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {devMatter.length > 0 && (
              <>
                <Divider />
                <TablePaginationLocalizationProvider>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={devMatter.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </TablePaginationLocalizationProvider>
              </>
            )}
          </Card>
        )}
      </Container>
    </>
  );
};

export default Grading;
